import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Header } from "../components/app/Header";
import { EarlyBirdBanner } from "../components/app/EarlyBirdBanner";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useI18n } from "../lib/i18n";
import { ArrowUpRight, ScanLine, BrainCircuit, Target, ShieldAlert, Upload, LineChart, Twitter, MessageCircle } from "lucide-react";

const Stat = ({ label, value, color }) => (
  <div className="flex items-center gap-3 px-4 py-2 border border-white/10 bg-white/[0.02]" data-testid={`stat-${label}`}>
    <span className="label-mono">{label}</span>
    <span className={`font-mono text-sm ${color || "text-white"}`}>{value}</span>
  </div>
);

export default function Landing() {
  const navigate = useNavigate();
  const { t } = useI18n();
  return (
    <div className="min-h-screen relative" data-testid="landing-page">
      <EarlyBirdBanner />
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 terminal-grid opacity-40 pointer-events-none" />
        <div className="absolute -top-32 -right-32 h-[420px] w-[420px] rounded-full bg-bull/10 blur-[120px] pointer-events-none" />
        <div className="mx-auto max-w-7xl px-6 md:px-8 pt-20 pb-24 relative">
          <div className="flex items-center gap-2 mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-bull animate-pulse-glow" />
            <span className="label-mono">{t("hero_status")}</span>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tighter leading-[0.95] max-w-4xl">
            {t("hero_title_1")}<br />
            <span className="text-neutral-400">{t("hero_title_2")}</span>
            <span className="text-bull">_</span>
          </h1>
          <p className="mt-6 max-w-2xl text-bull/90 font-mono text-sm tracking-wider uppercase">
            {t("brand_subtagline")}
          </p>
          <p className="mt-6 max-w-2xl text-neutral-400 text-lg leading-relaxed">
            {t("hero_desc")}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Button
              className="bg-white text-black hover:bg-neutral-200 rounded-none h-12 px-7 font-medium"
              onClick={() => navigate("/login?mode=signup")}
              data-testid="hero-get-started-btn"
            >
              {t("hero_cta_start")}
              <ArrowUpRight className="ml-2 h-4 w-4" strokeWidth={2} />
            </Button>
            <Button
              variant="outline"
              className="border-white/15 bg-transparent hover:bg-white/5 rounded-none h-12 px-7 text-white"
              onClick={() => document.getElementById("how")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="hero-how-btn"
            >
              {t("hero_cta_how")}
            </Button>
          </div>

          <div className="mt-12 flex flex-wrap gap-3">
            <Stat label={t("stat_model")} value="claude-sonnet-4.5" />
            <Stat label={t("stat_latency")} value="~6.2s" color="text-bull" />
            <Stat label={t("stat_output")} value="JSON · 11 fields" />
            <Stat label={t("stat_markets")} value="EQ / FX / CRYPTO" />
          </div>
        </div>

        {/* Faux terminal preview panel */}
        <div className="mx-auto max-w-7xl px-6 md:px-8 pb-20 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-8 surface-card p-6 relative overflow-hidden" data-testid="preview-chart">
              <div className="flex items-center justify-between mb-4">
                <span className="label-mono">CHART // BTCUSD · 4H</span>
                <span className="font-mono text-xs text-bull">● ANALYZING</span>
              </div>
              <div className="aspect-[16/8] terminal-grid relative border border-white/5">
                <svg viewBox="0 0 800 320" className="w-full h-full" preserveAspectRatio="none">
                  <polyline fill="none" stroke="#00FF88" strokeWidth="1.5" points="0,260 40,250 80,255 120,230 160,210 200,225 240,180 280,165 320,175 360,140 400,150 440,120 480,110 520,135 560,100 600,85 640,95 680,70 720,55 760,75 800,40" />
                  <polyline fill="none" stroke="rgba(255,255,255,0.15)" strokeDasharray="3 4" strokeWidth="1" points="0,150 800,150" />
                  <polyline fill="none" stroke="rgba(251,191,36,0.5)" strokeDasharray="2 3" strokeWidth="1" points="0,80 800,80" />
                </svg>
                <div className="scanline-overlay" />
              </div>
            </div>
            <div className="lg:col-span-4 space-y-4">
              <div className="surface-card p-5 glow-bull">
                <span className="label-mono">{t("prediction_label")}</span>
                <div className="mt-3 flex items-baseline gap-3">
                  <ArrowUpRight className="h-8 w-8 text-bull" strokeWidth={1.5} />
                  <span className="font-mono text-4xl font-medium text-bull">{t("direction_BULLISH")}</span>
                </div>
                <div className="mt-3 font-mono text-sm text-neutral-300">CONF <span className="text-bull">78%</span></div>
                <div className="mt-3 h-1 bg-white/5 overflow-hidden">
                  <div className="h-full bg-bull" style={{ width: "78%" }} />
                </div>
              </div>
              <div className="surface-card p-5 space-y-2.5 font-mono text-sm">
                <div className="flex justify-between"><span className="text-neutral-500">ENTRY</span><span className="text-white">68,420</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">TP1</span><span className="text-bull">71,200</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">TP2</span><span style={{color: "#FF8C00"}}>74,800</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">SL</span><span className="text-bear">66,100</span></div>
                <div className="flex justify-between border-t border-white/5 pt-2 mt-1"><span className="text-neutral-500">R / R</span><span className="text-bull">1 : 2.4</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="border-b border-white/10">
        <div className="mx-auto max-w-7xl px-6 md:px-8 py-20">
          <span className="label-mono">{t("social_label")}</span>
          <h2 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight">{t("social_title")}</h2>
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {[
              ["social_1_val", "social_1_lbl"],
              ["social_2_val", "social_2_lbl"],
              ["social_3_val", "social_3_lbl"],
              ["social_4_val", "social_4_lbl"],
            ].map(([v, l]) => (
              <div key={v} className="bg-[#0a0a0a] p-8">
                <div className="font-mono text-4xl sm:text-5xl font-medium text-bull tracking-tighter">{t(v)}</div>
                <div className="mt-2 label-mono">{t(l)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="border-b border-white/10">
        <div className="mx-auto max-w-7xl px-6 md:px-8 py-24">
          <span className="label-mono">{t("how_label")}</span>
          <h2 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight">{t("how_title")}</h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {[
              { n: "01", Icon: Upload, title: t("how_1_title"), body: t("how_1_body") },
              { n: "02", Icon: BrainCircuit, title: t("how_2_title"), body: t("how_2_body") },
              { n: "03", Icon: Target, title: t("how_3_title"), body: t("how_3_body") },
            ].map(({ n, Icon, title, body }) => (
              <div key={n} className="bg-[#0a0a0a] p-8 hover:bg-[#0e0e0e] transition-colors">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-neutral-500 tracking-[0.2em]">{n}</span>
                  <Icon className="h-5 w-5 text-bull" strokeWidth={1.5} />
                </div>
                <h3 className="mt-6 text-xl font-medium">{title}</h3>
                <p className="mt-2 text-neutral-400 text-sm leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="border-b border-white/10">
        <div className="mx-auto max-w-7xl px-6 md:px-8 py-24 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <span className="label-mono">{t("caps_label")}</span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight">
              {t("caps_title_1")}<br />{t("caps_title_2")}
            </h2>
            <p className="mt-6 text-neutral-400 leading-relaxed">{t("caps_body")}</p>
          </div>
          <div className="space-y-4">
            {[
              { Icon: LineChart, t: t("cap_pattern_t"), d: t("cap_pattern_d") },
              { Icon: Target, t: t("cap_levels_t"), d: t("cap_levels_d") },
              { Icon: ScanLine, t: t("cap_indicator_t"), d: t("cap_indicator_d") },
              { Icon: ShieldAlert, t: t("cap_risk_t"), d: t("cap_risk_d") },
            ].map(({ Icon, t: title, d }) => (
              <div key={title} className="flex gap-4 surface-card p-5">
                <div className="shrink-0 h-10 w-10 border border-white/10 flex items-center justify-center">
                  <Icon className="h-4 w-4 text-bull" strokeWidth={1.5} />
                </div>
                <div>
                  <h4 className="font-medium">{title}</h4>
                  <p className="mt-1 text-sm text-neutral-400">{d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-white/10">
        <div className="mx-auto max-w-3xl px-6 md:px-8 py-24">
          <span className="label-mono">{t("faq_label")}</span>
          <h2 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight">{t("faq_title")}</h2>
          <Accordion type="single" collapsible className="mt-10 border-t border-white/10">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <AccordionItem key={i} value={`q${i}`} className="border-b border-white/10" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-medium text-base hover:no-underline hover:text-bull py-5">
                  {t(`faq_q${i}`)}
                </AccordionTrigger>
                <AccordionContent className="text-neutral-400 leading-relaxed pb-5 pr-8">
                  {t(`faq_a${i}`)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="mx-auto max-w-4xl px-6 md:px-8 py-24 text-center">
          <h2 className="text-4xl sm:text-5xl font-semibold tracking-tighter">
            {t("cta_title_1")}<br /><span className="text-bull">{t("cta_title_2")}</span>
          </h2>
          <p className="mt-6 text-neutral-400 max-w-xl mx-auto">{t("cta_desc")}</p>
          <Button
            className="mt-10 bg-white text-black hover:bg-neutral-200 rounded-none h-12 px-8 font-medium"
            onClick={() => navigate("/login?mode=signup")}
            data-testid="cta-bottom-btn"
          >
            {t("cta_btn")}
          </Button>
        </div>
      </section>

      <footer className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 md:px-8 py-10 grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          <span className="font-mono text-[11px] text-neutral-500 tracking-[0.2em] uppercase">
            {t("footer_disclaimer")}
          </span>
          <div className="flex md:justify-center items-center gap-5 font-mono text-[11px] text-neutral-500 tracking-[0.18em] uppercase">
            <a href="#" className="hover:text-white">{t("footer_privacy")}</a>
            <a href="#" className="hover:text-white">{t("footer_terms")}</a>
            <a href="mailto:hello@candle.ai" className="hover:text-white">{t("footer_contact")}</a>
          </div>
          <div className="flex md:justify-end items-center gap-3">
            <a href="#" aria-label="Twitter" className="h-8 w-8 inline-flex items-center justify-center border border-white/10 hover:border-bull/40 hover:text-bull text-neutral-400 transition-colors">
              <Twitter className="h-3.5 w-3.5" strokeWidth={1.5} />
            </a>
            <a href="#" aria-label="Discord" className="h-8 w-8 inline-flex items-center justify-center border border-white/10 hover:border-bull/40 hover:text-bull text-neutral-400 transition-colors">
              <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.5} />
            </a>
            <Link to="/login" className="ml-2 font-mono text-[11px] text-neutral-500 hover:text-white tracking-[0.2em] uppercase">
              ACCESS_TERMINAL →
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
